import { Field, FieldProps } from 'formik';
import React from 'react';
import { FormattedMessage, injectIntl, InjectedIntlProps } from 'react-intl';
import styled, { keyframes } from 'styled-components';
import { COLORS, FONT_FAMILY, MQS } from '../constants/styles';
import ErrorMessage from './errorMessage/ErrorMessage';
import { IErrorMessage } from './errorMessage/errorMessage.type';
import Label from './Label';
import { specialCharacterTransformReverse } from '../validator/phraseValidator';
import { useSelector } from 'react-redux';
import { WarningIcon } from './icons/WarningIcon';
import { TickIcon } from './icons/TickIcon';
import { TranslationHelpers } from '../../../i18n/translationHelpers';

export interface IInputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
    autoComplete?: string;
    children?: React.ReactNode;
    error?: IErrorMessage;
    id: string;
    labelMessage?: FormattedMessage.MessageDescriptor | string;
    showErrorIcon?: boolean;
    value?: string;
    placeholder?: string;
    ref?: React.Ref<any>;
    hasTooltip?: boolean;
    convert?: boolean;
    onInputChange?: (str: string) => void;
    valid?: boolean;
    hiddenBorder?: boolean;
    showRedBorder?: boolean;
    onNewPasswordBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onNewPasswordFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

interface IInputFieldStyleProps extends React.InputHTMLAttributes<HTMLInputElement> {
    hasTooltip?: boolean;
    hiddenBorder?: boolean;
    error?: boolean;
    showRedBorder?: boolean;
}

const ValidityAnimation = keyframes`
    0% { transform: translateX(100%); }
    100% { transform: translateX(0); }
`;

export const Input = styled.input<IInputFieldStyleProps>`
    box-sizing: border-box;
    padding: 0 ${({ hiddenBorder }) => (hiddenBorder ? '0' : '0.6rem')};
    border: 1px solid
        ${({ error, showRedBorder }) => (error || showRedBorder ? COLORS.redTitle : COLORS.dark2)};
    background-color: transparent;
    display: flex;
    align-items: center;
    border: ${({ hiddenBorder }) => (hiddenBorder ? 'hidden' : 'auto')} !important;
    border-radius: 0.32rem;
    color: ${(props: { disabled?: boolean }) => (props.disabled ? COLORS.grey3 : COLORS.dark)};
    ${FONT_FAMILY.multipleRegularCss};
    font-weight: 300;
    width: 100%;
    font-size: 1rem;
    padding-right: ${(props: { hasTooltip?: boolean }) => (props.hasTooltip ? '28px' : '16px')};
    height: 2.5rem;
    justify-content: space-between;

    &:focus {
        outline: ${({ hiddenBorder }) => (hiddenBorder ? 'none' : '1px solid ' + COLORS.redTitle)};
        border: 1px solid ${COLORS.redTitle};
    }

    & + label {
        color: ${COLORS.grey7};
    }

    &:focus + label,
    &:-webkit-autofill + label {
        top: -25px;
        font-size: 0.875rem;
    }

    &:focus + label {
        color: ${COLORS.redTitle};
    }

    &:focus ~ .error {
        display: ${(props: { name?: string }) => (!props.name?.includes('password') ? 'none' : '')};
    }

    &[disabled] {
        opacity: 1; // required for iOS safari;
        -webkit-text-fill-color: ${COLORS.grey3};
        border-color: #dfdfdf;
        color: #dfdfdf;
    }

    &[type='search'] {
        -webkit-appearance: none;
    }
    & ~ .error {
        margin-left: 0.625rem;
    }
    & ~ .success {
        margin-left: 0.625rem;
    }
`;

const Validity = styled.div`
    position: absolute;
    top: 0.6rem;
    right: 0.625rem;
    overflow: hidden;
    svg {
        animation: ${ValidityAnimation} 0.75s ease;
    }
`;

const FieldGroup = styled.div`
    position: relative;
`;

const selectSpecialCharsAutoConvert = (state: any) =>
    state.app.clientConfig.specialCharsAutoConvert;

const InputField = ({
    autoComplete,
    children = null,
    error,
    id,
    labelMessage,
    name = id,
    required,
    value,
    placeholder,
    ref,
    className,
    onBlur,
    onInputChange,
    convert = true,
    valid = undefined,
    intl,
    onNewPasswordBlur,
    onNewPasswordFocus,
    ...rest
}: IInputFieldProps & InjectedIntlProps) => {
    const isConvert = (useSelector(selectSpecialCharsAutoConvert) ?? false) && convert;
    const ariaLabel =
        labelMessage && TranslationHelpers.isMessageDescriptor(labelMessage)
            ? intl.formatMessage(labelMessage)
            : labelMessage;

    return (
        <FieldGroup className={className}>
            {!placeholder && labelMessage && (
                <Label
                    fixed={true}
                    labelMessage={labelMessage}
                    required={required}
                    htmlFor={id}
                    error={error || valid === false}
                />
            )}
            <Field
                ref={ref}
                render={({ field }: FieldProps) => (
                    <Input
                        {...field}
                        autoComplete={autoComplete ? autoComplete : 'off'}
                        className="input-field"
                        id={id}
                        name={name}
                        value={value}
                        onBlur={(event) => {
                            if (isConvert) {
                                event.target.value = specialCharacterTransformReverse(
                                    event.target.value,
                                );
                            }
                            if (onBlur) {
                                onBlur(event);
                            } else {
                                field.onBlur(event);
                            }
                            if (onNewPasswordBlur) {
                                onNewPasswordBlur(event);
                            }
                        }}
                        onFocus={(event) => {
                            if (onNewPasswordFocus) {
                                onNewPasswordFocus(event);
                            }
                        }}
                        onChange={(event) => {
                            field.onChange(event);
                            onInputChange && onInputChange(event.target.value);
                        }}
                        placeholder={placeholder}
                        error={!!error || valid === false}
                        aria-label={ariaLabel}
                        {...rest}
                    />
                )}
            />
            <Validity>
                {valid === false ? <WarningIcon /> : valid !== undefined && <TickIcon />}
            </Validity>
            {children}
            {error ? <ErrorMessage message={error} /> : null}
        </FieldGroup>
    );
};

export default injectIntl(InputField);
